// Which association (tenant) this browser tab belongs to, and the request header
// that tells the backend about it for Atrionos staff.
//
// Production: every association lives at https://<slug>.atrionos.com, so the
// subdomain IS the tenant. Local dev, *.vercel.app previews and other hosts have no
// tenant subdomain: they default to Crestview, and `?tenant=<slug>` switches the tab
// to another association (remembered for the tab in sessionStorage).
//
// Header x-atrionos-hoa: sent ONLY for Super Admin (public.platform_owners) and only
// while the tab is in an association other than their home one. The database
// (get_my_hoa_id) and Edge Functions (_shared/callerAuth.ts) ignore it for everyone
// else, so residents' requests are unchanged byte for byte.

export const DEFAULT_TENANT_SLUG = 'crestview-eighty';
export const PLATFORM_DOMAIN = 'atrionos.com';
export const TENANT_HEADER = 'x-atrionos-hoa';

const TENANT_OVERRIDE_KEY = 'atrionos.tenantOverride';
const RESERVED_SUBDOMAINS = new Set(['www', 'app', 'admin', 'api', 'staging', 'mail', 'support', 'portal']);
const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/;

export function isValidTenantSlug(slug) {
  return typeof slug === 'string' && SLUG_PATTERN.test(slug) && !RESERVED_SUBDOMAINS.has(slug);
}

function readOverride(storage) {
  try {
    return storage?.getItem(TENANT_OVERRIDE_KEY) || null;
  } catch (_) {
    return null;
  }
}

function writeOverride(storage, slug) {
  try {
    storage?.setItem(TENANT_OVERRIDE_KEY, slug);
  } catch (_) {
    // Private mode or blocked storage: the override only lasts for this page load.
  }
}

function defaultStorage() {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch (_) {
    return null;
  }
}

// True when the host is <slug>.atrionos.com, i.e. the address itself names the tenant.
export function isTenantHost(hostname) {
  const host = String(hostname || '').toLowerCase().split(':')[0];
  if (!host.endsWith(`.${PLATFORM_DOMAIN}`)) return false;
  const sub = host.slice(0, -(PLATFORM_DOMAIN.length + 1));
  return !sub.includes('.') && isValidTenantSlug(sub);
}

export function resolveTenantSlug(location = (typeof window !== 'undefined' ? window.location : null), storage = defaultStorage()) {
  if (!location) return DEFAULT_TENANT_SLUG;
  const host = String(location.hostname || '').toLowerCase();

  if (isTenantHost(host)) {
    return host.split('.')[0];
  }

  // No tenant in the address: dev, previews, the bare vercel.app alias.
  let requested = null;
  try {
    requested = new URLSearchParams(location.search || '').get('tenant');
  } catch (_) {
    requested = null;
  }
  if (requested && isValidTenantSlug(requested.toLowerCase())) {
    const slug = requested.toLowerCase();
    writeOverride(storage, slug);
    return slug;
  }

  const remembered = readOverride(storage);
  if (remembered && isValidTenantSlug(remembered)) return remembered;

  return DEFAULT_TENANT_SLUG;
}

// The portal address for an association, as seen from the current page. On a
// tenant host it is the sibling subdomain; elsewhere (dev, previews) it is this
// same origin with ?tenant=.
export function tenantPortalUrl(slug, path = '/portal', location = (typeof window !== 'undefined' ? window.location : null)) {
  if (location && isTenantHost(location.hostname)) {
    return `https://${slug}.${PLATFORM_DOMAIN}${path}`;
  }
  const origin = location?.origin || '';
  const separator = path.includes('?') ? '&' : '?';
  return `${origin}${path}${separator}tenant=${encodeURIComponent(slug)}`;
}

let staffTenantSlug = null;

export function setStaffTenantHeader(slug) {
  staffTenantSlug = isValidTenantSlug(slug) ? slug : null;
}

export function getStaffTenantHeader() {
  return staffTenantSlug;
}

// fetch used by the Supabase client: adds the tenant header only while a staff
// member works inside an association other than their home one.
export function tenantAwareFetch(input, init = {}) {
  if (!staffTenantSlug) return fetch(input, init);
  const headers = new Headers(init.headers || (input instanceof Request ? input.headers : undefined));
  headers.set(TENANT_HEADER, staffTenantSlug);
  return fetch(input, { ...init, headers });
}
